import React, {useEffect, useMemo, useState} from "react";
import Table from "../../../system/components/table";
import {useArchive} from "../../../models/archive/use-archive";
import {useSelector} from "react-redux";
import {useTask} from "../../../models/task/use-task";
import {useProject} from "../../../models/project/use-project";
import {useGoal} from "../../../models/goal/use-goal";
import {useDeliverable} from "../../../models/deliverable/use-deliverable";
import TaskSidebar from "../../projects/components/task-sidebar";
import ProjectSidebar from "../../projects/components/project-sidebar";
import GoalSidebar from "../../goals/components/goal-sidebar";
import DeliverableSidebar from "../../projects/components/deliverable-sidebar";
import {usePermission} from "../../../system/usePermission";
import {useTranslate} from "../../../system/useTranslate";
import Icon from "../../../system/components/icon";
import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import "../../tasks/components/task-list-filter.css";

const archiveDropdownPt = {
    root: {className: "zofino-filter-dropdown"},
    input: {className: "text-xs font-normal white-space-nowrap flex align-items-center"},
    trigger: {className: "zofino-filter-dropdown__trigger"},
    panel: {className: "zofino-filter-dropdown__panel"},
    item: {className: "text-xs font-normal"},
};
const archiveButtonPt = {
    label: {className: "font-normal"},
};
const archiveFilterButtonClass = (selected) => (
    `h-3rem text-xs font-normal py-0 px-2 white-space-nowrap border-1 align-items-center justify-content-center ${selected ? "p-button-primary border-primary" : "border-300 text-color surface-100 hover:text-primary hover:border-primary"}`
);

const archivePermission = {
    project: "archive-project",
    task: "canManageTasks",
    deliverable: "canManageTasks",
    goal: "create-new-goals",
};

function Archive() {
    const {config, workspaceMembershipData} = useSelector(
        (state) => state.app
    );

    const translate = useTranslate();
    const permission = usePermission();

    const archive = useArchive();
    const task = useTask();
    const project = useProject();
    const goal = useGoal();
    const deliverable = useDeliverable();

    const [archives, setArchives] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedType, setSelectedType] = useState(null);
    const [selectedArchivedBy, setSelectedArchivedBy] = useState(null);
    const [selectedArchive, setSelectedArchive] = useState(undefined);

    const typeOptions = useMemo(
        () => [
            {
                label: translate("archive.type-all"),
                value: null,
            },
            {
                label: translate("task"),
                value: "task",
            },
            {
                label: translate("project"),
                value: "project",
            },
            {
                label: translate("goal"),
                value: "goal",
            },
            {
                label: translate("deliverable"),
                value: "deliverable",
            },
        ],
        [translate]
    );

    /**
     * استخراج شناسه آرشیوکننده از ساختارهای مختلف داده
     */
    const getArchivedById = (archivedBy) => {
        if (!archivedBy) {
            return null;
        }

        if (typeof archivedBy === "string" || typeof archivedBy === "number") {
            return archivedBy;
        }

        return (
            archivedBy.member?.id ||
            archivedBy.member?.documentId ||
            archivedBy.id ||
            archivedBy.documentId ||
            archivedBy.user?.id ||
            archivedBy.user?.documentId ||
            null
        );
    };

    /**
     * استخراج نام آرشیوکننده برای نمایش در Dropdown
     */
    const getArchivedByLabel = (archivedBy) => {
        if (!archivedBy) {
            return translate("archive.archived-by-unknown");
        }

        if (typeof archivedBy === "string") {
            return archivedBy;
        }

        if (typeof archivedBy === "number") {
            return String(archivedBy);
        }

        // TODO: Backend needs to provide the 'archivedBy' workspace-membership
        // relation with its 'member' populated (e.g. archivedBy.member.fullname).
        // If the backend only returns a raw id, the dropdown stays empty.
        const member = archivedBy.member;
        return (
            member?.fullname ||
            [member?.firstname, member?.lastname].filter(Boolean).join(" ") ||
            member?.username ||
            member?.email ||
            archivedBy.name ||
            archivedBy.username ||
            archivedBy.email ||
            archivedBy.user?.name ||
            archivedBy.user?.username ||
            archivedBy.user?.email ||
            translate("archive.archived-by-unknown")
        );
    };


    const archiverOptions = useMemo(() => {
        const uniqueArchivers = new Map();

        archives.forEach((item) => {
            const archivedById = getArchivedById(item.archivedBy);

            if (
                archivedById !== null &&
                archivedById !== undefined
            ) {
                uniqueArchivers.set(String(archivedById), {
                    label: getArchivedByLabel(item.archivedBy),
                    value: archivedById,
                });
            }
        });

        return [
            {
                label: translate("archive.archived-by-all"),
                value: null,
            },
            ...Array.from(uniqueArchivers.values()).sort((a, b) =>
                String(a.label).localeCompare(String(b.label))
            ),
        ];
    }, [archives, translate]);

    const filteredArchives = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        return archives.filter((item) => {
            const itemName = String(item?.name || "").toLowerCase();

            if (query && !itemName.includes(query)) {
                return false;
            }

            if (selectedType && item?.type !== selectedType) {
                return false;
            }

            if (selectedArchivedBy !== null) {
                const archivedById = getArchivedById(item.archivedBy);

                if (
                    String(archivedById) !==
                    String(selectedArchivedBy)
                ) {
                    return false;
                }
            }

            return true;
        });
    }, [
        archives,
        searchText,
        selectedType,
        selectedArchivedBy,
    ]);

    const normalize = ({
                           tasks = [],
                           projects = [],
                           goals = [],
                           deliverables = [],
                       }) => {
        return [
            ...tasks.map((item) => ({
                name: item.title,
                type: "task",
                documentId: item.documentId,
                archivedBy: item.archivedBy,
                archivedAt: item.archivedAt,
            })),

            ...projects.map((item) => ({
                name: item.title,
                type: "project",
                documentId: item.documentId,
                archivedBy: item.archivedBy,
                archivedAt: item.archivedAt,
            })),

            ...goals.map((item) => ({
                name: item.title,
                type: "goal",
                documentId: item.documentId,
                archivedBy: item.archivedBy,
                archivedAt: item.archivedAt,
            })),

            ...deliverables.map((item) => ({
                name: item.title,
                type: "deliverable",
                documentId: item.documentId,
                archivedBy: item.archivedBy,
                archivedAt: item.archivedAt,
            })),
        ];
    };

    const getArchives = async () => {
        const data = await archive.getArchives(
            workspaceMembershipData?.workspace?.id
        );

        const normalizedData = normalize(data);

        const permittedArchives = normalizedData.filter((item) =>
            permission.can(archivePermission[item.type])
        );

        setArchives(permittedArchives);
    };

    useEffect(() => {
        if (workspaceMembershipData?.workspace?.id) {
            getArchives();
        }
    }, [workspaceMembershipData]);

    const openSidebar = async (item) => {
        let data = null;

        switch (item.type) {
            case "task":
                data = await task.getArchivedTask(item.documentId);
                break;

            case "project":
                data = await project.getArchivedProject(item.documentId);
                break;

            case "goal":
                data = await goal.getArchivedGoal(item.documentId);
                break;

            case "deliverable":
                data = await deliverable.getArchivedDeliverable(
                    item.documentId
                );
                break;

            default:
                return;
        }

        setSelectedArchive({
            type: item.type,
            data,
        });
    };

    const actions = {
        onUnArchiveClicked: async (item) => {
            if (!permission.can(archivePermission[item.type], true)) {
                return;
            }

            if (item.type === "project") {
                await project.updateProject(item.documentId, {
                    archivedAt: null,
                    archivedBy: null,
                });
            }

            if (item.type === "task") {
                await task.updateTask(item.documentId, {
                    archivedAt: null,
                    archivedBy: null,
                });
            }

            if (item.type === "goal") {
                await goal.updateGoal(item.documentId, {
                    archivedAt: null,
                    archivedBy: null,
                });
            }

            if (item.type === "deliverable") {
                await deliverable.updateDeliverable(item.documentId, {
                    archivedAt: null,
                    archivedBy: null,
                });
            }

            await getArchives();
        },
    };

    const tableConfig = archive.prepareTable({
        actions,
        config,
    });

    return (
        <div className="flex flex-column h-full min-h-0">
            <div className="zofino-task-list-filters flex flex-column w-full p-2 md:p-3 gap-2">
                <div className="flex flex-column xl:flex-row align-items-stretch xl:align-items-center gap-2 w-full xl:flex-wrap">
                    <div className="flex flex-column sm:flex-row gap-2 w-full xl:w-auto">
                        {typeOptions.filter((option) => option.value).map((option) => (
                            <Button
                                key={option.value}
                                outlined={selectedType !== option.value}
                                pt={archiveButtonPt}
                                className={`w-full sm:w-5rem ${archiveFilterButtonClass(selectedType === option.value)}`}
                                label={option.label}
                                onClick={() => setSelectedType((previous) => previous === option.value ? null : option.value)}
                            />
                        ))}
                    </div>

                    <div className="flex flex-column sm:flex-row gap-2 w-full xl:w-auto">
                        <Dropdown
                            className="w-full sm:w-9rem text-xs font-normal"
                            pt={archiveDropdownPt}
                            value={selectedArchivedBy}
                            options={archiverOptions}
                            dropdownIcon={<Icon icon="mi mi:keyboard_arrow_down" className="text-sm"/>}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={translate("archive.archived-by-all")}
                            onChange={(event) => setSelectedArchivedBy(event.value)}
                        />
                    </div>

                    <IconField className="w-full flex-1">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            className="w-full h-3rem p-inputtext-sm surface-100 text-xs font-normal"
                            value={searchText}
                            placeholder={`${translate("archive.search-placeholder")}...`}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </IconField>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-auto w-full">
                <div className="m-2 md:m-3 border-1 border-100 border-round-3xl shadow-1 pt-3 pb-3 surface-50">
                    <Table
                        showFooter={false}
                        fieldsConfig={tableConfig.fieldsConfig}
                        showHeader={false}
                        fields={tableConfig.fields}
                        value={filteredArchives}
                        style={null}
                        onRowClick={(event) => {
                            openSidebar(event.value);
                    }}
                />
            </div>
            </div>

            <TaskSidebar
                disabled
                mode="archive"
                selectedTask={
                    selectedArchive?.type === "task"
                        ? selectedArchive.data
                        : undefined
                }
                setSelectedTask={setSelectedArchive}
            />

            <GoalSidebar
                disabled
                mode="archive"
                goalData={
                    selectedArchive?.type === "goal"
                        ? selectedArchive.data
                        : undefined
                }
                setGoalData={setSelectedArchive}
                onHide={() => setSelectedArchive(undefined)}
            />

            <ProjectSidebar
                disabled
                mode="archive"
                projectData={
                    selectedArchive?.type === "project"
                        ? selectedArchive.data
                        : undefined
                }
                setProjectData={setSelectedArchive}
                onHide={() => setSelectedArchive(undefined)}
            />

            <DeliverableSidebar
                disabled
                mode="archive"
                selectedDeliverable={
                    selectedArchive?.type === "deliverable"
                        ? selectedArchive.data
                        : undefined
                }
                setSelectedDeliverable={setSelectedArchive}
                tasks={[]}
            />
        </div>
    );
}

export default Archive;



// import React, {useEffect, useMemo, useState} from 'react';
// import Table from "../../../system/components/table";
// import {useArchive} from "../../../models/archive/use-archive";
// import {useDispatch, useSelector} from "react-redux";
// import {useTask} from "../../../models/task/use-task";
// import {useProject} from "../../../models/project/use-project";
// import {useGoal} from "../../../models/goal/use-goal";
// import {setWorkspaceMembership} from "../../../system/app-slice";
// import TaskSidebar from "../../projects/components/task-sidebar";
// import {useDeliverable} from "../../../models/deliverable/use-deliverable";
// import ProjectSidebar from "../../projects/components/project-sidebar";
// import GoalSidebar from "../../goals/components/goal-sidebar";
// import DeliverableSidebar from "../../projects/components/deliverable-sidebar";
// import {usePermission} from "../../../system/usePermission";
// import {useTranslate} from "../../../system/useTranslate";
// import {IconField} from "primereact/iconfield";
// import {InputIcon} from "primereact/inputicon";
// import {InputText} from "primereact/inputtext";
// import {Dropdown} from "primereact/dropdown";
//
// const archivePermission = {
//     project: "archive-project",
//     task: "canManageTasks",
//     deliverable: "canManageTasks",
//     goal: "create-new-goals",
// };
//
// function Archive(props) {
//     const {config, workspaceMembershipData} = useSelector((state) => state.app);
//     const translate = useTranslate();
//     const [archives, setArchives] = useState([])
//     const [searchText, setSearchText] = useState("");
//     const [selectedType, setSelectedType] = useState(null);
//     const archive = useArchive();
//     const task = useTask();
//     const project = useProject();
//     const goal = useGoal();
//     const deliverable = useDeliverable();
//     // const [selectedArchive, setSelectedArchive] = useState()
//
//     const dispatch = useDispatch();
//     const [selectedArchive, setSelectedArchive] = useState();
//     const permission = usePermission();
//
//     const typeOptions = useMemo(() => [
//         {label: translate("archive.type-all"), value: null},
//         {label: translate("task"), value: "task"},
//         {label: translate("project"), value: "project"},
//         {label: translate("goal"), value: "goal"},
//         {label: translate("deliverable"), value: "deliverable"},
//     ], [translate]);
//
//     const filteredArchives = useMemo(() => archives.filter((item) => {
//         const q = searchText.trim().toLowerCase();
//         if (q && !(item?.name || "").toLowerCase().includes(q)) return false;
//         if (selectedType && item?.type !== selectedType) return false;
//         return true;
//     }), [archives, searchText, selectedType]);
//     const openSidebar = async (item) => {
//         let data = null;
//         switch (item.type) {
//             case "task":
//                 data = await task.getArchivedTask(item.documentId);
//                 break;
//
//             case "project":
//                 data = await project.getArchivedProject(item.documentId);
//                 break;
//
//             case "goal":
//                 data = await goal.getArchivedGoal(item.documentId);
//                 break;
//             case "deliverable":
//                 data = await deliverable.getArchivedDeliverable(item.documentId);
//                 break;
//
//             default:
//                 return;
//         }
//         setSelectedArchive({type : item.type, data});
//     };
//
//
//     useEffect(() => {
//         if (workspaceMembershipData) {
//             getArchives();
//         }
//     }, [workspaceMembershipData]);
//
//
//     const normalize = ({tasks, projects, goals, deliverables}) => {
//         let _data = [
//             ...tasks?.map(t => ({
//                 name: t.title,
//                 type: "task",
//                 documentId: t.documentId,
//                 archivedBy: t.archivedBy,
//                 archivedAt: t.archivedAt
//             })),
//             ...projects?.map(p => ({
//                 name: p.title,
//                 type: "project",
//                 documentId: p.documentId,
//                 archivedBy: p.archivedBy,
//                 archivedAt: p.archivedAt
//             })),
//             ...goals?.map(g => ({
//                 name: g.title,
//                 type: "goal",
//                 documentId: g.documentId,
//                 archivedBy: g.archivedBy,
//                 archivedAt: g.archivedAt
//             })),
//             ...deliverables?.map(g => ({
//                 name: g.title,
//                 type: "deliverable",
//                 documentId: g.documentId,
//                 archivedBy: g.archivedBy,
//                 archivedAt: g.archivedAt
//             })),
//         ]
//         return _data;
//     }
//
//     const getArchives = async () => {
//         let _data = await archive.getArchives(workspaceMembershipData?.workspace.id)
//         // dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, projects:_data.projects}}));
//
//         setArchives(normalize(_data).filter((item) => (
//             permission.can(archivePermission[item.type])
//         )))
//     }
//
//
//     const actions = {
//         onUnArchiveClicked: async (item) => {
//             if (!permission.can(archivePermission[item.type], true)) return;
//             if (item.type === "project") {
//                 await project.updateProject(item.documentId, {archivedAt: null, archivedBy: null})
//             }
//             if (item.type === "task") {
//                 await task.updateTask(item.documentId, {archivedAt: null, archivedBy: null})
//             }
//             if (item.type === "goal") {
//                 await goal.updateGoal(item.documentId, {archivedAt: null, archivedBy: null})
//             }
//             if (item.type === "deliverable") {
//                 await deliverable.updateDeliverable(item.documentId, {archivedAt: null, archivedBy: null})
//             }
//             getArchives();
//         }
//     }
//
//     return (
//         <div className="p-4 overflow-auto h-full">
//             <div className="flex flex-wrap gap-2 align-items-center justify-content-between pb-3">
//                 <IconField className="w-20rem">
//                     <InputIcon className="pi pi-search"/>
//                     <InputText value={searchText}
//                                placeholder={`${translate("archive.search-placeholder")}...`}
//                                className="py-2 text-sm w-full"
//                                onChange={(e) => {setSearchText(e.target.value)}}
//                     />
//                 </IconField>
//                 <Dropdown value={selectedType}
//                           options={typeOptions}
//                           onChange={(e) => {setSelectedType(e.value)}}
//                           placeholder={translate("archive.type-all")}
//                           className="text-sm"
//                           panelClassName="rtl text-sm"
//                 />
//             </div>
//             <div className="border-1 border-100 border-round-3xl shadow-1 pt-3 pb-3 surface-50">
//                 <Table showFooter={false}
//                        fieldsConfig={archive.prepareTable({actions, config}).fieldsConfig}
//                        showHeader={false}
//                        fields={archive.prepareTable({config}).fields}
//                        value={filteredArchives}
//                        style={null}
//                        onRowClick={(e) => {
//                            openSidebar(e.value)
//                        }}
//                 />
//             </div>
//
//             <TaskSidebar
//                 disabled
//                 mode="archive"
//                 selectedTask={selectedArchive?.type === "task" ? selectedArchive?.data : undefined}
//                 setSelectedTask={setSelectedArchive}
//             />
//
//             <GoalSidebar
//                 disabled
//                 mode="archive"
//                 setGoalData={setSelectedArchive}
//                 goalData={selectedArchive?.type === "goal" ? selectedArchive?.data : undefined}
//                 onHide={() => setSelectedArchive(undefined)}
//             />
//             <ProjectSidebar
//             disabled
//             mode="archive"
//             onHide={() => setSelectedArchive(undefined)}
//             projectData={selectedArchive?.type === "project" ? selectedArchive?.data : undefined}
//             setProjectData={setSelectedArchive}
//         />
//             <DeliverableSidebar
//                 disabled
//                 mode="archive"
//                 selectedDeliverable={selectedArchive?.type === "deliverable" ? selectedArchive?.data : undefined}
//                 setSelectedDeliverable={setSelectedArchive}
//                 tasks={[]}
//             />
//
//         </div>
//     );
// }
//
// export default Archive;
